import React, { memo } from "react";
import { Skeleton, Typography, Box, CircularProgress, Fade } from "@mui/material";
import Grid from "@mui/material/Grid2";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import InsightsIcon from "@mui/icons-material/Insights";

// Define animations once to avoid re-injecting into the DOM
const SPIN_KEYFRAMES = {
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
};

const COMMON_BOX_STYLES = {
  width: "100%",
  maxWidth: { sm: "100%", md: "1700px" },
  position: "relative",
};

const STICKY_INDICATOR_STYLES = {
  position: "fixed",
  zIndex: 2000,
  display: "flex",
  alignItems: "center",
  gap: 1.2,
  px: 1.5,
  py: 0.8,
  borderRadius: 2,
  background: "linear-gradient(135deg, rgba(25,118,210,0.12), rgba(25,118,210,0.05))",
  backdropFilter: "blur(6px)",
  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  border: "1px solid rgba(25,118,210,0.15)",
  ...SPIN_KEYFRAMES,
};

const SpinnerView = memo(({ position, message }) => (
  <Fade in timeout={400}>
    <Box>
      {/* Top-right floating indicator */}
      <Box sx={{ ...STICKY_INDICATOR_STYLES, top: position?.top ?? 12, right: position?.right ?? 20 }}>
        <AutorenewIcon
          sx={{
            color: "primary.main",
            fontSize: 18,
            animation: "spin 1.5s linear infinite",
          }}
        />
        <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 600 }}>
          {message}
        </Typography>
      </Box>

      {/* Main center loader */}
      <Box sx={{ height: 260, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
        <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
          <CircularProgress size={48} thickness={4} sx={{ color: "primary.main", opacity: 0.9 }} />
          <InsightsIcon sx={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", fontSize: 22, color: "primary.main" }} />
        </Box>
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>
          Updating analytics dashboard...
        </Typography>
      </Box>
    </Box>
  </Fade>
));

const SkeletonView = memo(({ message }) => (
  <>
    <Typography component="h2" variant="h6" sx={{ mb: 2 }}>{message}</Typography>
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid size={12}><Skeleton variant="rectangular" width="30%" height={300} /></Grid>
      <Grid size={12}><Skeleton variant="rectangular" width="30%" height={150} /></Grid>
    </Grid>
  </>
));

const LoadingComponent = memo(({ loading = {} }) => {
  const { 
    type = "spinner", 
    position, 
    message = "Syncing data..." 
  } = loading;

  // Use a simple switch or direct render to avoid creating objects on the fly
  return (
    <Box sx={COMMON_BOX_STYLES}>
      {type === "spinner" ? (
        <SpinnerView position={position} message={message} />
      ) : (
        <SkeletonView message={message} />
      )}
    </Box>
  );
});

LoadingComponent.displayName = "LoadingComponent";
SpinnerView.displayName = "SpinnerView";
SkeletonView.displayName = "SkeletonView";

export default LoadingComponent;